import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { dashboardApi, monitorApi } from '../services/api.js';

const DashboardContext = createContext(null);
const REFRESH_INTERVAL = 30000;
const MONITOR_INTERVAL = 10000;

export function DashboardProvider({ children }) {
  const [data, setData] = useState(null);
  const [monitorData, setMonitorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedExtension, setSelectedExtension] = useState(null);
  const [showMissedCalls, setShowMissedCalls] = useState(false);

  const intervalRef = useRef(null);
  const monitorRef = useRef(null);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await dashboardApi.getStats();
      setData(res.data);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMonitor = useCallback(async () => {
    try {
      const res = await monitorApi.getAgents();
      setMonitorData(res.data);
    } catch (err) {
      console.error('Monitor fetch error:', err);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    fetchMonitor();
    intervalRef.current = setInterval(fetchDashboard, REFRESH_INTERVAL);
    monitorRef.current = setInterval(fetchMonitor, MONITOR_INTERVAL);
    return () => {
      clearInterval(intervalRef.current);
      clearInterval(monitorRef.current);
    };
  }, [fetchDashboard, fetchMonitor]);

  const refresh = useCallback(() => {
    setLoading(true);
    fetchDashboard();
    fetchMonitor();
  }, [fetchDashboard, fetchMonitor]);

  return (
    <DashboardContext.Provider value={{
      data, monitorData, loading, error, lastUpdated, refresh,
      selectedAgent, setSelectedAgent,
      selectedExtension, setSelectedExtension,
      showMissedCalls, setShowMissedCalls
    }}>
      {children}
    </DashboardContext.Provider>
  );
}

export function useDashboard() {
  const context = useContext(DashboardContext);
  if (!context) throw new Error('useDashboard must be used within DashboardProvider');
  return context;
}
